import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useGetCustomHeaders } from "@vega-api/custom/stores/useGetCustomHeaders";
import { useSessionStorage } from "@vega-core/hooks/useSessionStorage";

import { TableFilters } from "../TableFilters";
import {
  type FilterConfig,
  type FilterState,
  FilterType,
  type FilterValue,
  type FilterValuesByType,
  type TableFilterId,
  type TableFiltersConfig,
} from "../types";

import { useFiltersLength } from "./useFiltersLength";

// Map a config to a strongly-typed state: id -> value type
type FilterStateFrom<C extends readonly FilterConfig[]> = {
  [F in C[number] as F["id"]]: FilterValuesByType[F["type"]];
};

export type UseTableFiltersParams<C extends readonly FilterConfig[]> = {
  id: TableFilterId;
  config: Omit<TableFiltersConfig, "filters"> & { readonly filters: C };
  isLoading?: boolean;
  syncWithURL?: boolean;
  showSavedFilters?: boolean;
  persistInSession?: boolean;
  applyContextInPersistenceKeys?: boolean;
  onFilterChange?: () => void;
};

export const useCreateTableContextKey = ({
  tableFilterId,
  applyContextInPersistenceKeys,
}: {
  tableFilterId: TableFilterId;
  applyContextInPersistenceKeys?: boolean;
}) => {
  const { contextKey } = useGetCustomHeaders();
  let tableKey = `tableFilters_${tableFilterId}`;

  if (applyContextInPersistenceKeys) {
    tableKey += `_${contextKey}`;
  }

  return encodeURIComponent(tableKey);
};

export function useTableFilters<const C extends readonly FilterConfig[]>(
  params: UseTableFiltersParams<C>,
) {
  const {
    id,
    config,
    isLoading = false,
    syncWithURL = false,
    showSavedFilters = false,
    persistInSession = true,
    applyContextInPersistenceKeys = true,
    onFilterChange,
  } = params;
  const [searchParams, setSearchParams] = useSearchParams();
  const { contextKey, previousContextKey, resetPreviousContextKey } =
    useGetCustomHeaders();
  const shownFiltersLength = useFiltersLength({
    maxShownFilters: config.maxShownFilters,
  });

  const tableKey = useCreateTableContextKey({
    tableFilterId: id,
    applyContextInPersistenceKeys,
  });

  const getDefaultFilterState = useCallback((): FilterStateFrom<C> => {
    const entries = config.filters.map((f) => [
      f.id,
      f.defaultValue ?? getDefaultValue(f.type),
    ]);

    return Object.fromEntries(entries) as FilterStateFrom<C>;
  }, [config.filters]);

  // Session storage hook (only used when persistInSession is true)
  const [sessionFilterState, setSessionFilterState] = useSessionStorage<
    FilterStateFrom<C>
  >(tableKey, getDefaultFilterState());

  const [filterState, setFilterState] = useState<FilterStateFrom<C>>(() => {
    if (syncWithURL) {
      // Initialize from URL parameters (highest priority)
      const entries = config.filters.map((f) => {
        const paramKey = `${tableKey}.${f.id}`;
        const paramValue = searchParams.get(paramKey);

        if (paramValue) {
          // URL parameter exists, use it
          return [f.id, deserializeFilterValue(paramValue, f.type)];
        } else if (persistInSession) {
          // No URL parameter, but session persistence is enabled - use session value
          const sessionValue =
            sessionFilterState[f.id as keyof FilterStateFrom<C>];

          return [
            f.id,
            coerceFilterValue(
              sessionValue ?? f.defaultValue ?? getDefaultValue(f.type),
              f.type,
            ),
          ];
        } else {
          // No URL parameter and no session persistence - use default
          return [f.id, f.defaultValue ?? getDefaultValue(f.type)];
        }
      });

      return Object.fromEntries(entries) as FilterStateFrom<C>;
    } else if (persistInSession) {
      // Initialize from session storage (second priority)
      // Merge session values with per-filter defaults
      const entries = config.filters.map((f) => {
        const sessionValue =
          sessionFilterState[f.id as keyof FilterStateFrom<C>];

        return [
          f.id,
          coerceFilterValue(
            sessionValue ?? f.defaultValue ?? getDefaultValue(f.type),
            f.type,
          ),
        ];
      });

      return Object.fromEntries(entries) as FilterStateFrom<C>;
    } else {
      // Default initialization (lowest priority)
      return getDefaultFilterState();
    }
  });

  const setFilterValue = useCallback(
    <K extends keyof FilterStateFrom<C>>(
      filterId: K,
      value: FilterStateFrom<C>[K],
    ) => {
      setFilterState((prev) => ({ ...prev, [filterId]: value }));
    },
    [],
  );

  const resetFilterState = useCallback(() => {
    const defaultState = getDefaultFilterState();
    setFilterState(defaultState);
  }, [getDefaultFilterState]);

  // The public reset is a user action, so it reports through onFilterChange
  // like any other filter edit; the context-switch reset below is programmatic
  // housekeeping and stays silent.
  const resetFilters = useCallback(() => {
    resetFilterState();
    onFilterChange?.();
  }, [resetFilterState, onFilterChange]);

  // Reset filters when contextKey changes
  useEffect(() => {
    // Reset if the context key has actually changed
    // Skip reset if applyContextInPersistenceKeys is false (filters don't depend on context)
    if (
      applyContextInPersistenceKeys &&
      previousContextKey !== undefined &&
      previousContextKey !== contextKey
    ) {
      resetFilterState();
      resetPreviousContextKey();
    }
  }, [
    applyContextInPersistenceKeys,
    contextKey,
    previousContextKey,
    resetFilterState,
    resetPreviousContextKey,
  ]);

  const hasActiveFilters = useMemo(
    () =>
      config.filters
        .filter((filter) => filter.isVisible !== false)
        .some((filter) => {
          const value = filterState[filter.id as keyof FilterStateFrom<C>];

          return hasValue(value, filter.type);
        }),
    [filterState, config.filters],
  );

  const activeFilterCount = useMemo(
    () =>
      config.filters
        .filter((filter) => filter.isVisible !== false)
        .reduce((count, filter) => {
          const value = filterState[filter.id as keyof FilterStateFrom<C>];

          if (hasValue(value, filter.type)) {
            if (
              (filter.type === FilterType.Multiselect ||
                filter.type === FilterType.MultiSelectNestedV2 ||
                filter.type === FilterType.MultiSelectNested) &&
              Array.isArray(value)
            ) {
              return count + value.length;
            }

            return count + 1;
          }

          return count;
        }, 0),
    [filterState, config.filters],
  );

  // Mirrors RenderFilters' own overflow math (Filters/useFiltersLength) so a
  // consumer can tell whether the shared "more filters" drawer trigger will
  // actually render, e.g. to gate a custom affordance that opens it.
  const visibleFilters = config.filters.filter(
    (filter) => filter.isVisible !== false,
  );
  const hasOverflowFilters =
    visibleFilters.filter((filter) => !filter.hideFromModal).length >
    visibleFilters
      .slice(0, shownFiltersLength)
      .filter((filter) => !filter.hideFromModal).length;

  // Bridge: convert typed values to UI shape expected by TableFilters
  const uiFilterState = useMemo(() => {
    const entries = config.filters.map((f) => [
      f.id,
      { value: filterState[f.id as keyof FilterStateFrom<C>], type: f.type },
    ]);

    return Object.fromEntries(entries) as Record<
      string,
      { value: FilterValuesByType[FilterType]; type: FilterType }
    >;
  }, [config.filters, filterState]);

  const handleFilterChange = useCallback(
    (filterId: string, value: FilterValue) => {
      setFilterState((prev) => ({
        ...prev,
        [filterId]: value as FilterStateFrom<C>[keyof FilterStateFrom<C>],
      }));
      onFilterChange?.();
    },
    [onFilterChange],
  );

  const RenderFilters = useMemo(
    () => (
      <TableFilters
        activeFilterCount={activeFilterCount}
        applyContextInPersistenceKeys={applyContextInPersistenceKeys}
        config={config}
        filterState={uiFilterState as FilterState}
        hasActiveFilters={hasActiveFilters}
        id={id}
        isLoading={isLoading}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        showSavedFilters={showSavedFilters}
      />
    ),
    [
      config,
      uiFilterState,
      handleFilterChange,
      resetFilters,
      hasActiveFilters,
      activeFilterCount,
      isLoading,
      showSavedFilters,
      id,
      applyContextInPersistenceKeys,
    ],
  );

  // Two sync effects with different triggers — kept separate to avoid unnecessary work:
  // 1. searchParams changes → read URL into state (external navigation)
  // 2. filterState changes → write state to URL (user interaction)
  // internalUpdateRef prevents loops: state→URL sets flag, URL→state skips when flag is set
  const internalUpdateRef = useRef(false);

  useEffect(() => {
    if (!syncWithURL || internalUpdateRef.current) {
      internalUpdateRef.current = false;

      return;
    }

    const entries = config.filters.map((f) => {
      const paramKey = `${tableKey}.${f.id}`;
      const paramValue = searchParams.get(paramKey);
      const currentValue = filterState[f.id as keyof FilterStateFrom<C>];

      if (paramValue) {
        return [f.id, deserializeFilterValue(paramValue, f.type)];
      }

      return [f.id, currentValue];
    });

    const newState = Object.fromEntries(entries) as FilterStateFrom<C>;
    const newJson = JSON.stringify(newState);
    const currentJson = JSON.stringify(filterState);

    if (newJson !== currentJson) {
      setFilterState(newState);
    }
  }, [searchParams]);

  // Sync filterState → URL
  useEffect(() => {
    if (!syncWithURL) return;

    const applyFilterParams = (params: URLSearchParams): boolean => {
      let hasChanges = false;

      config.filters.forEach((filter) => {
        const paramKey = `${tableKey}.${filter.id}`;
        const value = filterState[filter.id as keyof FilterStateFrom<C>];

        if (hasValue(value, filter.type)) {
          const serializedValue = serializeFilterValue(value);

          if (params.get(paramKey) !== serializedValue) {
            params.set(paramKey, serializedValue);
            hasChanges = true;
          }
        } else if (params.has(paramKey)) {
          params.delete(paramKey);
          hasChanges = true;
        }
      });

      return hasChanges;
    };

    if (!applyFilterParams(new URLSearchParams(searchParams))) return;

    internalUpdateRef.current = true;
    // Functional update: params not owned by this hook (e.g. a page's own
    // summaryStatus param) may change in the same batch — building from the
    // closure's searchParams would resurrect their old values.
    setSearchParams(
      (current) => {
        const merged = new URLSearchParams(current);
        applyFilterParams(merged);

        return merged;
      },
      { replace: true },
    );
  }, [filterState, syncWithURL, tableKey]);

  useEffect(() => {
    if (!persistInSession) return;
    setSessionFilterState(filterState);
  }, [filterState, persistInSession, setSessionFilterState]);

  return {
    filterState,
    setFilterValue,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
    hasOverflowFilters,
    RenderFilters,
  } as const;
}

export const allFilterDefaultValues = Object.values(FilterType).map((type) =>
  JSON.stringify(getDefaultValue(type)),
);
export const checkIfFilterValueChanged = (
  value: FilterValue,
  type: FilterType,
) => hasValue(value, type);

export const sanitizeFilterState = (
  filterState?: FilterState,
): FilterState | undefined => {
  if (!filterState) return undefined;
  const sanitizedFilterState: FilterState = {};
  Object.entries(filterState).forEach(([key, value]) => {
    const filterId = key;
    const typedValue = value as { value: FilterValue; type: FilterType };
    const filterValue = typedValue.value;
    const filterType = typedValue.type;

    if (filterValue && checkIfFilterValueChanged(filterValue, filterType)) {
      sanitizedFilterState[filterId] = {
        value: filterValue as FilterValuesByType[FilterType],
        type: filterType,
      };
    } else {
      sanitizedFilterState[filterId] = {
        value: getDefaultValue(filterType),
        type: filterType,
      };
    }
  });

  return sanitizedFilterState;
};

export function getDefaultValue(
  type: FilterType,
): FilterValuesByType[FilterType] {
  switch (type) {
    case FilterType.Search:
      return "";
    case FilterType.Dropdown:
      return undefined as FilterValuesByType[FilterType.Dropdown];
    case FilterType.SearchableDropdown:
      return undefined as FilterValuesByType[FilterType.SearchableDropdown];
    case FilterType.Multiselect:
      return [] as FilterValuesByType[FilterType.Multiselect];
    case FilterType.MultiSelectNested:
      return [] as FilterValuesByType[FilterType.MultiSelectNested];
    case FilterType.DateRange:
      return {
        from: undefined,
        to: undefined,
      } as FilterValuesByType[FilterType.DateRange];
    case FilterType.Switch:
      return false as FilterValuesByType[FilterType.Switch];
    case FilterType.MultiSelectNestedV2:
      return [] as FilterValuesByType[FilterType.MultiSelectNestedV2];
  }
}

function hasValue(value: unknown, type: FilterType): boolean {
  switch (type) {
    case FilterType.Search:
      return typeof value === "string" && value.trim() !== "";
    case FilterType.Dropdown:
    case FilterType.SearchableDropdown:
      return (
        Boolean(value) &&
        typeof (value as { value?: unknown }).value !== "undefined"
      );
    case FilterType.Multiselect:
      return Array.isArray(value) && value.length > 0;
    case FilterType.DateRange: {
      const v = value as { from?: unknown; to?: unknown } | undefined;

      return Boolean(v?.from || v?.to);
    }
    case FilterType.Switch:
      return value === true;
    case FilterType.MultiSelectNested:
      return Array.isArray(value) && value.length > 0;
    case FilterType.MultiSelectNestedV2:
      return Array.isArray(value) && value.length > 0;
  }
}

export function serializeFilterValue(value: unknown): string {
  try {
    // Don't encode here - URLSearchParams will handle encoding
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

export function deserializeFilterValue(
  serialized: string,
  type: FilterType,
): FilterValuesByType[FilterType] {
  try {
    const parsed = JSON.parse(serialized);

    // Convert to expected runtime shapes per filter type
    switch (type) {
      case FilterType.DateRange: {
        // Accept { from: isoString, to: isoString } and convert to Dates
        if (
          parsed &&
          typeof parsed === "object" &&
          ("from" in parsed || "to" in parsed)
        ) {
          const from = (parsed as { from?: string }).from;
          const to = (parsed as { to?: string }).to;

          return {
            from: typeof from === "string" ? new Date(from) : undefined,
            to: typeof to === "string" ? new Date(to) : undefined,
          } as FilterValuesByType[FilterType.DateRange];
        }

        return getDefaultValue(FilterType.DateRange);
      }

      default:
        return coerceFilterValue(parsed, type);
    }
  } catch {
    return getDefaultValue(type);
  }
}

// A filter's persisted value (URL param / sessionStorage) can predate a config
// change that switched the filter's type (e.g. dropdown -> multiselect behind a
// feature flag). A shape mismatch would crash `.map`/`.slice` consumers, so fall
// back to the type's default instead of trusting the stored shape.
function coerceFilterValue(
  value: unknown,
  type: FilterType,
): FilterValuesByType[FilterType] {
  switch (type) {
    case FilterType.Multiselect:
    case FilterType.MultiSelectNested:
    case FilterType.MultiSelectNestedV2:
      return Array.isArray(value)
        ? (value as FilterValuesByType[FilterType])
        : getDefaultValue(type);
    case FilterType.Dropdown:
    case FilterType.SearchableDropdown:
      return Array.isArray(value)
        ? getDefaultValue(type)
        : (value as FilterValuesByType[FilterType]);
    default:
      return value as FilterValuesByType[FilterType];
  }
}
